import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Row,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import Router from 'next/router';
import { ascending } from 'd3';
import { CaretDownIcon, CaretUpIcon } from '@radix-ui/react-icons';
import { IAreaGraph, IContributor } from '@/types/explorer';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/shadcn/table';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/shadcn/card';
import { sanitizeUsername } from '@/utils/strings';
import useExplorerData from '@/hooks/useExplorerData';
import AreaChart from '@/components/ui/charts/AreaChart';
import { prettyDate } from '@/utils/dates';
import Contribute from '@/components/ui/shared/Contribute';
import { getContributors } from '@/api/contributors';
import { Button } from '@/components/shadcn/button';
import CustomTooltip from '../charts/CustomTooltip';

const LIMIT = 100;

const columnHelper = createColumnHelper<IContributor>();
const cols = [
  {
    id: 'username',
    header: 'Contributor',
    enableSorting: true,
    cell: ({ row }: { row: Row<IContributor> }) => (
      <div className="text-xs md:text-sm">
        {sanitizeUsername(row.getValue('username'))}
      </div>
    ),
  },
  {
    id: 'createdAt',
    header: 'Joined',
    enableSorting: true,
    sortDescFirst: true,
    cell: ({ row }: { row: Row<IContributor> }) => (
      <div className="text-xs md:text-sm">
        {prettyDate(row.getValue('createdAt'))}
      </div>
    ),
  },
  {
    id: 'lastSeen',
    header: 'Last Seen',
    enableSorting: true,
    sortDescFirst: true,
    cell: ({ row }: { row: Row<IContributor> }) => (
      <div className="text-xs md:text-sm">
        {prettyDate(row.getValue('lastSeen'))}
      </div>
    ),
  },
];

export default function Contributors() {
  const { data: explorerData } = useExplorerData();
  const [contributorsList, setContributors] = useState<IContributor[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [pagination, setPagination] = useState({
    pageSize: LIMIT,
    pageIndex: 0,
  });
  const [sorting, setSorting] = useState([{ id: 'createdAt', desc: true }]);
  const order = !sorting.length ? 'DESC' : sorting[0].desc ? 'DESC' : 'ASC';
  const field = sorting.length ? sorting[0].id : 'DESC';
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const columns = useMemo(
    () =>
      cols.map(({ id, header, cell, enableSorting, sortDescFirst }: any) => ({
        ...columnHelper.accessor(id, {
          header,
        }),
        cell,
        enableSorting,
        sortDescFirst,
      })),
    [],
  );

  const fetchList = useCallback(
    async (skip = 0, orderBy: string, order: 'ASC' | 'DESC') => {
      setIsLoading(true);
      const contributors: IContributor[] = await getContributors(
        LIMIT,
        skip,
        orderBy,
        order.toLowerCase() === 'asc',
      );
      setHasNextPage(contributors.length === LIMIT);
      setContributors(contributors);
      setIsDataLoaded(true);
      setIsLoading(false);
    },
    [setContributors],
  );

  useEffect(() => {
    fetchList(pagination.pageIndex * LIMIT, field, order);
  }, [fetchList, pagination.pageIndex, field, order]);

  let graphData: IAreaGraph[] = [];

  const stats = explorerData?.stats;

  const total = stats?.byDay?.[stats?.mostRecentDay]?.totalContributors || 0;
  if (stats) {
    graphData = Object.entries(stats?.byDay)
      .map(([date, value]) => ({
        date,
        value: value.totalContributors,
      }))
      .sort((a, b) => ascending(a.date, b.date));
  }

  const tableData = React.useMemo(
    () => (isLoading ? [] : contributorsList),
    [isLoading, contributorsList],
  );

  const table = useReactTable({
    data: tableData,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    enableSortingRemoval: false,
    rowCount: total,
    state: {
      pagination,
      sorting: [
        {
          id: field,
          desc: order === 'DESC',
        },
      ],
    },
  });

  const from =
    table.getState().pagination.pageIndex *
      table.getState().pagination.pageSize +
    1;
  const to =
    from +
    Math.min(
      (table.getState().pagination.pageIndex + 1) *
        table.getState().pagination.pageSize,
      contributorsList.length - 1,
    );

  return (
    <div className="flex h-full flex-col lg:flex-row">
      <div className="flex h-1/2 w-full shrink-0 flex-col gap-2 px-4 py-2 md:gap-4 md:p-4 lg:h-full lg:w-1/2">
        <div className="no-scrollbar h-full flex-1 overflow-scroll rounded-md border [&>div]:overflow-visible">
          <Table className="h-full table-fixed">
            <TableHeader className="sticky top-0 bg-neutral-800">
              {table.getHeaderGroups().map(headerGroup => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => {
                    return (
                      <TableHead
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        data-order={index}
                        className="h-4 w-1/4 cursor-pointer p-2 data-[order='0']:w-1/2 md:h-12 md:px-4">
                        <div className="flex cursor-pointer items-center gap-1 px-0 text-[10px] leading-none md:text-sm">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}

                          {header.column.getIsSorted() === 'asc' ? (
                            <CaretUpIcon />
                          ) : header.column.getIsSorted() === 'desc' ? (
                            <CaretDownIcon />
                          ) : null}
                        </div>
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map(row => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer leading-none hover:bg-neutral-900"
                  onClick={() =>
                    Router.push(
                      `/network/contributors/${row.getValue('username')}`,
                    )
                  }>
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id} className="p-2 md:px-4 md:py-3">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {isDataLoaded ? (
          <div className="flex w-full items-center justify-end space-x-2 text-neutral-0">
            <div className="flex-1 text-[10px] md:text-xs">
              Showing {from} to {to} of {total} contributors
            </div>
            <div className="space-x-2">
              <Button
                variant="outline"
                size="sm"
                className="text-[10px] md:text-xs"
                onClick={table.previousPage}
                disabled={!table.getCanPreviousPage()}>
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-[10px] md:text-xs"
                onClick={table.nextPage}
                disabled={!hasNextPage}>
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </div>
      <div className="no-scrollbar flex w-full flex-col overflow-visible bg-neutral-700 p-4 text-neutral-0 lg:w-1/2 lg:overflow-auto">
        <div className="flex flex-1 flex-col gap-6 ">
          <Card className="flex h-44 w-full flex-1 flex-col overflow-hidden rounded border-none bg-neutral-900 md:h-56 md:rounded-lg">
            <CardHeader className="p-2 pb-1 text-xs md:p-6 md:pb-2 md:text-base">
              <CardTitle>Contributors</CardTitle>
            </CardHeader>
            <CardContent className="flex max-h-12 min-h-[200px] flex-1 flex-col p-2 pt-0 md:max-h-full md:p-6 md:pt-0">
              <p className="mb-1 text-base text-azure-400 md:text-2xl">
                {Math.round(total).toLocaleString('en-US')}
              </p>
              <div className="w-full flex-1">
                <AreaChart
                  data={graphData}
                  customTooltip={(props: any) => (
                    <CustomTooltip
                      line1={`${Math.round(
                        props?.payload[0]?.value || 0,
                      ).toLocaleString('en-US')} Contributors`}
                      {...props}
                    />
                  )}
                />
              </div>
            </CardContent>
          </Card>
          <Contribute />
        </div>
      </div>
    </div>
  );
}
